import { createElement, forwardRef } from 'react';
import { setAlpha } from '../../../design-tokens';
import { DesignTokens, renderInlineCssVars, useDesignTokens } from '../../../theme';
import { getListTokens } from '../../ListBox/__internal__/utils';
import {
  BaseMenuSection,
  BaseMenuItem,
  BaseMenuProps,
  CustomMenuSection,
  MenuListDesignProps,
  ItemLocation,
  isCustomSection,
  MenuListItem,
  MenuListSection,
} from '.';

import cx from 'classnames';
import cn from './MenuListDesign.css.json';
import './MenuListDesign.css';

export const MenuListDesign = forwardRef<HTMLDivElement, MenuListDesignProps>(
  function MenuListDesign(props, ref) {
    const dt = useDesignTokens();
    const tokenOverrides = props.tokens?.();
    const tokens = {
      ...renderInlineCssVars(
        {
          ...getMenuThemeTokens(dt),
          'disabled-opacity': dt.enableNovaTokens ? 1 : 0.3,
          'disabled-color': dt.enableNovaTokens
            ? dt.stateStandardNova.disabled.fg
            : dt.stateStandard.disabled.fg,
          'font-weight-thick': dt.enableNovaTokens
            ? dt.fontNova.weight.thick
            : dt.font.weight.thick,
          'heading-size-medium': dt.enableNovaTokens ? dt.fontNova.size.h5 : dt.font.size.h5,
          'font-family': tokenOverrides?.fontFamily ?? 'inherit',
        },
        'menu'
      ),
      ...renderInlineCssVars(
        {
          'stretch-xs': dt.spacing.insetStretch.xs.padding,
          'squish-s': dt.spacing.insetSquish.s.padding,
          l: dt.spacing.core.l,
          m: dt.spacing.core.m,
          s: dt.spacing.core.s,
          xs: dt.spacing.core.xs,
        },
        'menu-spacing'
      ),
    };

    return (
      <div
        ref={ref}
        className={cx(cn.listContainer, props.className)}
        tabIndex={props.tabIndex ?? 0}
        role="menu"
        onKeyDown={props.onKeyDown}
        style={{
          ...tokens,
          ...props.style,
        }}
      >
        <ul className={cx(cn.list)} role="presentation" style={{ ...getListTokens(dt) }}>
          {props.sections.map((section, sectionIndex) => {
            if (isCustomSection(section)) {
              return section.render({
                index: sectionIndex,
                key: sectionIndex,
                focusLocation:
                  props.focusLocation?.[0][0] === sectionIndex ? props.focusLocation : null,
                onItemHovered: props.onItemHovered,
              });
            }
            const hasMenuItemRightIcon = section.items.some(
              (item) => item.closable || item.subSections
            );
            return (
              <MenuListSection
                key={sectionIndex}
                section={section}
                index={sectionIndex}
                disabled={props.disabled}
                onSubmenuClosed={() => {
                  props.onSubmenuClosed?.(props.menuLevel);
                }}
              >
                {section.items.map((item, itemIndex) => {
                  if (section.selectedKey !== undefined)
                    item.checked = section.selectedKey === item.key;
                  else if (section.selectedIndex !== undefined)
                    item.checked = section.selectedIndex === itemIndex;
                  return (
                    <MenuListItem
                      key={`${itemIndex}-${item.text}`}
                      ref={sectionIndex === 0 && itemIndex === 0 ? props.firstItemRef : undefined}
                      item={item}
                      disabled={item.disabled || props.disabled}
                      closable={item.closable}
                      index={itemIndex}
                      listIndex={getListIndex([sectionIndex, itemIndex], props.sections)}
                      sectionIndex={sectionIndex}
                      additionalAriaText={item.additionalAriaText}
                      onSelected={props.onItemSelected}
                      onClosed={props.onItemClosed}
                      showFocus={props.showFocus}
                      focusLocation={props.focusLocation}
                      onItemHovered={props.onItemHovered}
                      onSubmenuClosed={props.onSubmenuClosed}
                      testId={item.testId}
                      isRadio={section.selectedIndex !== undefined}
                      menuHasIndentation={hasIndentation(props.sections)}
                      tokens={props.tokens}
                      menuLevel={props.menuLevel}
                      hasMenuItemRightIcon={hasMenuItemRightIcon}
                      onEscape={props.onKeyDown}
                      onSpace={props.onKeyDown}
                    />
                  );
                })}
              </MenuListSection>
            );
          })}
        </ul>
      </div>
    );
  }
);

function getMenuThemeTokens(dt: DesignTokens) {
  if (dt.enableNovaTokens) {
    return {
      'title-fg': dt.stateStandardNova.normal.fg,
      'divider-bd': setAlpha(dt.c.colorUiBorder, 0.5),
    };
  } else {
    switch (dt.themeVariant) {
      case 'dark':
        return {
          'title-fg': dt.c.white,
          'divider-bd': dt.c.white15,
        };
      case 'high-contrast':
        return {
          'title-fg': dt.c.white,
          'divider-bd': dt.c.white,
        };
      default:
        return {
          'title-fg': dt.c.n_3,
          'divider-bd': dt.c.n3,
        };
    }
  }
}

function getListIndex(location: ItemLocation, sections: BaseMenuProps['sections']): number {
  const [sectionIndex, itemIndex] = location;
  const itemCounts = sections.map((section) =>
    isCustomSection(section) ? 0 : section.items.length
  );
  const itemsInPreviousSections = itemCounts.slice(0, sectionIndex).reduce((a, b) => a + b, 0);
  return itemsInPreviousSections + itemIndex;
}

// Determine if any item has an indentation greater than 0
export function hasIndentation(sections: ReadonlyArray<BaseMenuSection | CustomMenuSection>) {
  function _checkItems(items: ReadonlyArray<BaseMenuItem>): boolean {
    // make sure `items` is iterable
    // https://stackoverflow.com/a/53106917/2295034
    if (Symbol.iterator in Object(items) === false) return false;
    for (const { indentation, subSections } of items) {
      if ((indentation as number) > 0) return true;
      if (subSections) return hasIndentation(subSections);
    }
    return false;
  }
  return sections.some((section) => _checkItems((section as BaseMenuSection).items));
}
